import type { GameLogEntry as GameLogEntryType } from '../types';
import { getTeamIndicatorStyle, getVisibleTextColor } from '../utils/colorUtils';

interface GameLogEntryProps {
  entry: GameLogEntryType;
  isNew?: boolean;
}

function formatLocalTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function GameLogEntry({ entry, isNew }: GameLogEntryProps) {
  const isScoreEvent = entry.eventType === 'SCORE' || entry.eventType === 'SCORE_ADJUSTMENT';
  const isAnnotation = entry.eventType === 'ANNOTATION';
  const isPeriodEvent = entry.eventType === 'PERIOD_START' || entry.eventType === 'PERIOD_END';
  const isGenericEvent = isAnnotation || isPeriodEvent;
  const isMiss = entry.eventType === 'MISS';
  const indicatorStyle = getTeamIndicatorStyle(entry.teamColor);
  const localTime = formatLocalTime(entry.timestamp);

  return (
    <div
      className={`py-2 px-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
        isNew ? 'log-entry-new bg-yellow-50' : ''
      } ${isAnnotation ? 'bg-blue-50' : ''} ${isMiss ? 'bg-red-50' : ''}`}
    >
      <div className="flex items-start gap-3">
        {/* Team color indicator (or special icon for generic events) */}
        {isGenericEvent ? (
          <div className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-500">
            {isAnnotation ? '📝' : '⏱️'}
          </div>
        ) : (
          <div
            className="w-4 h-4 rounded-full mt-0.5 flex-shrink-0"
            style={{
              backgroundColor: indicatorStyle.backgroundColor,
              border: indicatorStyle.border,
              boxSizing: 'border-box',
            }}
          />
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm">
            {/* Clock time */}
            {entry.clockTime && (
              <span className="font-mono text-gray-400 text-xs">
                {entry.clockTime}
              </span>
            )}
            {/* Period */}
            <span className="text-xs text-gray-400">
              P{entry.period}
            </span>
            {/* Local time */}
            <span className="text-xs text-gray-300">
              {localTime}
            </span>
          </div>

          {/* Description */}
          <div className={`text-sm mt-0.5 ${isMiss ? 'text-red-600' : 'text-gray-700'}`}>
            {!isGenericEvent && entry.teamAbbreviation && (
              <>
                <span className="font-medium" style={{ color: getVisibleTextColor(entry.teamColor) }}>
                  {entry.teamAbbreviation}
                </span>
                {' '}
              </>
            )}
            {entry.description}
          </div>
        </div>

        {/* Score (for scoring plays and adjustments) */}
        {isScoreEvent && (
          <div className="text-right flex-shrink-0">
            <div className="text-sm font-bold text-gray-800">
              {entry.scoreAfter.home} - {entry.scoreAfter.away}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
